import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { BUSINESS_CHALLENGES, CreateProjectContext } from './consts';
import { Button, Input, isEmpty, RichTextEditor, santizeInput } from '@platform/components';
import { ROUTES } from '../routes/consts';
import { useContext, useState } from 'react';
import { SupportTypeInfoItem } from '@platform/client/schemas';
import { MyProjects, Projects } from '@platform/client';
import { DeliverablesList, FormBlock } from './utils';
import { navigate } from 'wouter/use-browser-location';
import { useQueryClient } from '@tanstack/react-query';

type FormValues = {
  projectPhoto: string; // TODO: Correct type. Or, just remove?
  /* Trainer */
  trainingDeliverables: { value: string }[];
  /* Call */
  callOutcomesDeliverables: { value: string }[];
  /* Mentor */
  mentorOutcomesDeliverables: { value: string }[];
  /* Coach */
  coachOutcomesDeliverables: { value: string }[];
  // TODO: the Participation Agreement blurb, which mentions EY for some reason
  /* Consultant */
  projectTitle: string;
  consultantOutcomesDeliverables: { value: string }[];
  projectBkgRichText: string;
  learningOutcomesRichText: string;
};

const FinalDetailsStep = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmissionError, setIsSubmissionError] = useState(false);
  const queryClient = useQueryClient();
  const createProjectMutator = Projects.useCreateProjectOld({
    mutation: {
      onMutate() {
        setIsSubmitting(true);
      },
      onError: () => {
        setIsSubmitting(false);
        // Displays a generic "something went wrong" error
        setIsSubmissionError(true);
      },
      onSuccess: () => {
        setIsSubmitting(false);
        queryClient.invalidateQueries({
          queryKey: [...MyProjects.getGetOrgUserMyProjectsQueryKey()],
        });
        navigate(`platform${ROUTES.DASHBOARD}${ROUTES.MY_PROJECTS}`);
      },
    },
  });
  const { businessChallenge, skills, supportTypes } = useContext(CreateProjectContext);

  const { control, watch, handleSubmit } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      trainingDeliverables: [{ value: '' }],
      callOutcomesDeliverables: [{ value: '' }],
      mentorOutcomesDeliverables: [{ value: '' }],
      coachOutcomesDeliverables: [{ value: '' }],
      consultantOutcomesDeliverables: [{ value: '' }],
    },
  });

  let isSubmitEnabled = true;

  // Determine if submit should be enabled by checking if all required fields for each support type are filled

  if (supportTypes.has('Trainer')) {
    isSubmitEnabled =
      isSubmitEnabled && watch('trainingDeliverables').some((d) => !isEmpty(d.value));
  }
  if (supportTypes.has('Call')) {
    isSubmitEnabled =
      isSubmitEnabled && watch('callOutcomesDeliverables').some((d) => !isEmpty(d.value));
  }
  if (supportTypes.has('Mentor')) {
    isSubmitEnabled =
      isSubmitEnabled && watch('mentorOutcomesDeliverables').some((d) => !isEmpty(d.value));
  }
  if (supportTypes.has('Coach')) {
    isSubmitEnabled =
      isSubmitEnabled && watch('coachOutcomesDeliverables').some((d) => !isEmpty(d.value));
  }
  if (supportTypes.has('Consultant')) {
    isSubmitEnabled =
      isSubmitEnabled && watch('consultantOutcomesDeliverables').some((d) => !isEmpty(d.value));
    isSubmitEnabled =
      isSubmitEnabled &&
      !isEmpty(watch('projectTitle')) &&
      !isEmpty(watch('learningOutcomesRichText')) &&
      !isEmpty(watch('projectBkgRichText'));
  }

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    const supportTypeAnswers: SupportTypeInfoItem[] = [];

    if (supportTypes.has('Trainer')) {
      supportTypeAnswers.push({
        roleType: 'trainer',
        deliverables: data.trainingDeliverables.map((training) => santizeInput(training.value)),
      });
    }

    if (supportTypes.has('Call')) {
      supportTypeAnswers.push({
        // This role type is not a bug; see the documentation for CallProjectParams
        roleType: 'call',
        deliverables: data.callOutcomesDeliverables.map((training) => santizeInput(training.value)),
      });
    }

    if (supportTypes.has('Mentor')) {
      supportTypeAnswers.push({
        roleType: 'mentor',
        deliverables: data.mentorOutcomesDeliverables.map((training) =>
          santizeInput(training.value),
        ),
      });
    }

    if (supportTypes.has('Coach')) {
      supportTypeAnswers.push({
        roleType: 'coach',
        deliverables: data.coachOutcomesDeliverables.map((training) =>
          santizeInput(training.value),
        ),
      });
    }

    if (supportTypes.has('Consultant')) {
      supportTypeAnswers.push({
        // This role type is not a bug; see the documentation for ConsultantProjectParams
        roleType: 'consultant',
        deliverables: data.consultantOutcomesDeliverables.map((training) =>
          santizeInput(training.value),
        ),
        projectTitle: data.projectTitle,
        projectBackground: santizeInput(data.projectBkgRichText),
        learningOutcomes: santizeInput(data.learningOutcomesRichText),
      });
    }

    createProjectMutator.mutate({
      data: {
        businessChallenge:
          BUSINESS_CHALLENGES.find((bc) => bc.name === businessChallenge)?.value || '',
        skills: Array.from(skills),
        cohorts: null,
        supportTypeInfo: supportTypeAnswers,
      },
    });
  };

  return (
    <>
      <div>Please complete the following fields to finalize your submission.</div>
      <div className='w-2xl flex flex-col gap-4'>
        {/* Trainer */}
        {supportTypes.has('Trainer') && (
          <FormBlock header='Final Details For Your Trainer Request'>
            <p className='text-sm font-medium text-gray-500'>
              What training topic would help you and your team?
            </p>
            <DeliverablesList control={control} fieldKey='trainingDeliverables' />
          </FormBlock>
        )}
        {/* Call */}
        {supportTypes.has('Call') && (
          <FormBlock header='Final Details For Your Call Request'>
            <p className='text-sm font-medium text-gray-500'>Final Details For Your Call Request</p>
            <DeliverablesList control={control} fieldKey='callOutcomesDeliverables' />
          </FormBlock>
        )}
        {/* Mentor */}
        {supportTypes.has('Mentor') && (
          <FormBlock header='Final Details For Your Mentor Request'>
            <p className='text-sm font-medium text-gray-500'>
              Final Details For Your Mentor Request
            </p>
            <DeliverablesList control={control} fieldKey='mentorOutcomesDeliverables' />
          </FormBlock>
        )}
        {/* Coach */}
        {supportTypes.has('Coach') && (
          <FormBlock header='Final Details For Your Coach Request'>
            <p className='text-sm font-medium text-gray-500'>
              What are you hoping to get out of a coach?
            </p>
            <DeliverablesList control={control} fieldKey='coachOutcomesDeliverables' />
          </FormBlock>
        )}
        {/* Consultant */}
        {supportTypes.has('Consultant') && (
          <FormBlock header='Final Details For Your Consultant Request'>
            <Controller
              control={control}
              name='projectTitle'
              render={({ field }) => (
                <Input
                  type='inline'
                  label='Project Title'
                  value={field.value || ''}
                  placeholder='Please enter a project title'
                  onChange={field.onChange}
                  maxLength={120}
                />
              )}
            />
            <p className='text-sm font-medium text-gray-500'>What are the expected deliverables?</p>
            <DeliverablesList control={control} fieldKey='consultantOutcomesDeliverables' />
            <Controller
              control={control}
              name='projectBkgRichText'
              render={({ field }) => (
                <RichTextEditor
                  label='Explain the background of the project and why your organization needs support'
                  value={field.value || ''}
                  onChange={(e) => {
                    if (e.target.value.length <= 5000) {
                      field.onChange(e.target.value);
                    }
                  }}
                  dataTestId='project-bkg-rte'
                />
              )}
            />
            <Controller
              control={control}
              name='learningOutcomesRichText'
              render={({ field }) => (
                <RichTextEditor
                  label='What can the experteer learn by working with you? (Optional)' // TODO: Figure out how we should indicate optional vs required
                  value={field.value || ''}
                  onChange={(e) => {
                    if (e.target.value.length <= 5000) {
                      field.onChange(e.target.value);
                    }
                  }}
                  dataTestId='learning-outcomes-rte'
                />
              )}
            />
          </FormBlock>
        )}
        <div className='w-fit mx-auto'>
          <Button
            variant='accent'
            size='lg'
            onPress={() => handleSubmit(onSubmit)()}
            isDisabled={!isSubmitEnabled || isSubmitting}
          >
            Submit for Review
          </Button>
        </div>
        {isSubmissionError && (
          <div className='text-center text-red-600'>
            There was an error submitting your project. Please try again or contact support.
          </div>
        )}
        <div className='mx-auto blue-links'>
          <a href='mailto:support@movingworlds.org'>Contact MovingWorlds</a> if you have questions
          or need support.
        </div>
      </div>
    </>
  );
};

export default FinalDetailsStep;
